import React from "react";

// Inline: **bold** and the Lumina signal markers [!] (watch) / [+] (good) / [-] (declining).
const FLAG = { "!": ["Watch", "ai-flag-watch"], "+": ["Good", "ai-flag-good"], "-": ["Declining", "ai-flag-down"] };
function Inline({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\[[!+-]\])/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    const f = p.match(/^\[([!+-])\]$/);
    if (f) return <span key={i} className={`ai-flag ${FLAG[f[1]][1]}`}>{FLAG[f[1]][0]}</span>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// Headings may arrive glued to the previous line ("…[!]### Key Signals"); split them first.
export function AiMarkdown({ text }) {
  const src = String(text || "").replace(/([^\n#])(#{2,4}\s)/g, "$1\n$2");
  const blocks = [];
  src.split("\n").forEach((raw, i) => {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) return;
    const h = line.match(/^\s*(#{2,4})\s*(.*)$/);
    if (h) { blocks.push(h[1].length === 2 ? <h3 key={i} className="ai-h2"><Inline text={h[2]} /></h3> : <h4 key={i} className="ai-h3"><Inline text={h[2]} /></h4>); return; }
    const b = line.match(/^(\s*)[-*•]\s+(.*)$/);
    if (b) { const lvl = Math.min(3, Math.floor(b[1].length / 2)); blocks.push(<div key={i} className={`ai-li ai-li-${lvl}`}><span className="ai-dot" /><span><Inline text={b[2]} /></span></div>); return; }
    blocks.push(<p key={i} className="ai-p"><Inline text={line.trim()} /></p>);
  });
  return <div className="ai-md">{blocks}</div>;
}

// Echo streams a JSON array; recover every complete object while it is still arriving.
function parseEcho(text) {
  try { const a = JSON.parse(text); if (Array.isArray(a)) return a; } catch (e) { /* partial */ }
  const out = []; let depth = 0, start = -1, inStr = false, esc = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inStr) { if (esc) esc = false; else if (c === "\\") esc = true; else if (c === '"') inStr = false; continue; }
    if (c === '"') inStr = true;
    else if (c === "{") { if (depth === 0) start = i; depth += 1; }
    else if (c === "}") { depth -= 1; if (depth === 0 && start >= 0) { try { out.push(JSON.parse(text.slice(start, i + 1))); } catch (e) { /* skip */ } start = -1; } }
  }
  return out;
}
const fmtDate = (d) => { const t = new Date(`${d}T00:00:00`); return isNaN(t) ? d : t.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }); };
const typeLabel = (t) => String(t || "message").replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase());

export function EchoMessages({ text, language }) {
  const msgs = parseEcho(String(text || ""));
  if (!msgs.length) return null;
  return (
    <div data-testid="echo-messages">
      <div className="ai-echo-sum">{msgs.length} scheduled patient message{msgs.length === 1 ? "" : "s"}{language ? ` · ${language}` : ""}</div>
      <ol className="ai-echo-list">
        {msgs.map((m, i) => (
          <li key={i} className="ai-echo-row" data-testid={`echo-msg-${i}`}>
            <span className={`ai-echo-dot ai-echo-${m.type || "other"}`} />
            <div className="ai-echo-card">
              <div className="ai-echo-top">
                <span className="ai-echo-date">{m.date ? fmtDate(m.date) : "Undated"}</span>
                <span className={`ai-echo-type ai-echo-t-${m.type || "other"}`}>{typeLabel(m.type)}</span>
              </div>
              {m.message ? <div className="ai-echo-msg">{m.message}</div> : null}
              {m.reason ? <div className="ai-echo-why"><b>Why:</b> {m.reason}</div> : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function AiSteps({ steps }) {
  if (!steps || !steps.length) return null;
  return (
    <div className="ai-steps" data-testid="lumina-steps">
      {steps.map((s) => (
        <span key={s.n} className={`ai-step ${s.done ? "done" : "run"}`}>{s.done ? "✓" : <i className="ai-spin" />}{s.label.replace(/\.\.\.$/, "")}</span>
      ))}
    </div>
  );
}

export const AI_PANEL_CSS = `
.cv2-inspanel.wide{width:min(820px,96vw)}
.cv2-summary.ai-plain{background:transparent;border:0;padding:0}
.cv2-summary .ai-echo-row{padding-left:0;margin:0}.cv2-summary .ai-echo-row:before{content:none}
.ai-md{font:500 13.5px/1.6 Inter,system-ui,sans-serif;color:#2C2C2A}
.ai-md .ai-h2{font:800 17px Inter,system-ui,sans-serif;color:#141412;margin:2px 0 12px;letter-spacing:-.01em}
.ai-md .ai-h3{font:750 11px Inter,system-ui,sans-serif;text-transform:uppercase;letter-spacing:.07em;color:#6C6C69;margin:22px 0 10px;padding-top:14px;border-top:1px solid #ECECEF}
.ai-md .ai-h3:first-child{border-top:0;padding-top:0;margin-top:0}
.ai-p{margin:0 0 10px}
.ai-li{display:flex;gap:9px;margin:0 0 7px}
.ai-li-0{background:#fff;border:1px solid #ECECEF;border-radius:10px;padding:10px 12px;margin-top:10px}
.ai-li-1{margin-left:18px;font-size:13px;color:#48484A}.ai-li-2,.ai-li-3{margin-left:34px;font-size:12.5px;color:#55554F}
.ai-dot{flex:none;width:6px;height:6px;border-radius:50%;background:#B5B5B1;margin-top:8px}
.ai-li-0 .ai-dot{background:#141412}
.ai-li strong{color:#141412;font-weight:700}
.ai-flag{display:inline-block;margin-left:6px;padding:1px 7px;border-radius:999px;font:700 10px Inter,system-ui,sans-serif;vertical-align:1px}
.ai-flag-watch{background:#FDF4E5;color:#9A5A00}.ai-flag-good{background:#E7F6EE;color:#0E7A4D}.ai-flag-down{background:#FDEDEC;color:#C4302A}
.ai-steps{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 14px}
.ai-step{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;font:600 11.5px Inter,system-ui,sans-serif;background:#F0ECFC;color:#5B3FB8}
.ai-step.done{background:#F1F1F4;color:#6C6C69}
.ai-spin{width:10px;height:10px;border-radius:50%;border:2px solid #CFC4F2;border-top-color:#5B3FB8;animation:aispin .8s linear infinite}
@keyframes aispin{to{transform:rotate(360deg)}}
.ai-echo-sum{font:700 12.5px Inter,system-ui,sans-serif;color:#0F7A6E;margin-bottom:14px}
.ai-echo-list{list-style:none;margin:0;padding:0;position:relative}
.ai-echo-list:before{content:"";position:absolute;left:5px;top:6px;bottom:6px;width:2px;background:#E6E6E4}
.ai-echo-row{position:relative;display:flex;gap:14px;padding-bottom:12px}
.ai-echo-dot{position:relative;z-index:1;flex:none;width:12px;height:12px;border-radius:50%;margin-top:14px;background:#9B9B97;box-shadow:0 0 0 3px #F7F7F9}
.ai-echo-reminder{background:#2F6FB3}.ai-echo-symptom_check{background:#0F9D8A}
.ai-echo-card{flex:1;min-width:0;background:#fff;border:1px solid #ECECEF;border-radius:11px;padding:12px 14px}
.ai-echo-top{display:flex;align-items:center;gap:8px;margin-bottom:7px}
.ai-echo-date{font:700 12.5px Inter,system-ui,sans-serif;color:#141412;font-variant-numeric:tabular-nums}
.ai-echo-type{padding:2px 8px;border-radius:999px;font:700 10.5px Inter,system-ui,sans-serif;background:#F1F1F4;color:#6C6C69}
.ai-echo-t-reminder{background:#EAF1FC;color:#2F6FB3}.ai-echo-t-symptom_check{background:#E5F5F2;color:#0F7A6E}
.ai-echo-msg{font:500 13px/1.55 Inter,system-ui,sans-serif;color:#2C2C2A}
.ai-echo-why{margin-top:8px;font:500 11.5px/1.45 Inter,system-ui,sans-serif;color:#8A8A86}
.ai-echo-why b{color:#6C6C69;font-weight:650}
`;
